<template>
  <article
    class="ritem-card"
    role="link"
    tabindex="0"
    :aria-label="'View run ' + info.name"
    @click="openRun"
    @keydown.enter.prevent="openRun"
    @keydown.space.prevent="openRun"
  >
    <div class="ritem-media" :class="{ 'ritem-media--empty': !raceImageUrl }">
      <img v-if="raceImageUrl" :src="raceImageUrl" class="ritem-image" alt="">
      <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <path d="M21 15l-5-5L5 21"/>
      </svg>
    </div>

    <div class="ritem-body">
      <div class="ritem-topline">
        <span class="ritem-status">{{ statusLabel }}</span>
        <span class="ritem-date">{{ info.date | date }}</span>
      </div>
      <h3 class="ritem-title">{{ info.name }}</h3>

      <div class="ritem-chip-row">
        <span v-if="durationLabel" class="ritem-chip ritem-chip--duration">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 7v5l3 2"/>
          </svg>
          {{ durationLabel }}
        </span>
        <span v-for="typeName in typeChips" :key="'type-' + typeName" class="ritem-chip ritem-chip--type">
          {{ formatChip(typeName) }}
        </span>
        <span v-if="!typeChips.length" class="ritem-chip ritem-chip--muted">No type</span>
      </div>

      <div v-if="visibleTags.length" class="ritem-chip-row ritem-chip-row--tags">
        <span v-for="tag in visibleTags" :key="'tag-' + tag.id" class="ritem-chip ritem-chip--tag">
          {{ tag.name }}
        </span>
        <span v-if="hiddenTagCount > 0" class="ritem-chip ritem-chip--muted">+{{ hiddenTagCount }}</span>
      </div>

      <div v-if="hasActionDetails" class="ritem-action-meta">
        <img v-if="actionImageUrl" :src="actionImageUrl" class="ritem-action-image" alt="">
        <span v-else class="ritem-action-icon">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 3h6v6"/>
            <path d="M10 14L21 3"/>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          </svg>
        </span>
        <span class="ritem-action-text" :title="actionMetaText">{{ actionMetaText }}</span>
        <a
          v-if="actionUrl"
          :href="actionUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="ritem-action-open"
          title="Open action URL"
          @click.stop
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 3h6v6"/>
            <path d="M10 14L21 3"/>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          </svg>
        </a>
      </div>

      <div class="ritem-actions">
        <div class="ritem-icon-actions">
          <router-link class="ritem-edit-btn" :to="{ path: editPath }" title="Edit run" @click.native.stop>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
          </router-link>
          <button v-if="$isDevMode()" class="ritem-delete-btn" @click.prevent.stop="$emit('delete', info.id)" title="Dev: Delete this race">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"/>
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: 'race-item',
  data: () => ({
    path: ''
  }),
  props: {
    info: {
      type: Object,
      required: true
    },
    type: {
      type: String
    }
  },
  computed: {
    raceImageUrl () {
      return this.info.imageUrl ||
        this.info.image_url ||
        this.info.ImageUrl ||
        this.info.bannerImageUrl ||
        this.info.bannerUrl ||
        this.info.banner_image_url ||
        this.info.top_banner_image_url ||
        ''
    },
    typeChips () {
      return [
        this.info.type,
        this.info.secondType || this.info.second_type,
        this.info.thirdType || this.info.third_type
      ].filter(Boolean)
    },
    durationLabel () {
      if (this.info.duration === null || this.info.duration === undefined || this.info.duration === '') {
        return ''
      }
      return `${this.info.duration} min`
    },
    visibleTags () {
      const tags = Array.isArray(this.info.tags) ? this.info.tags : []
      return tags.slice(0, 3)
    },
    hiddenTagCount () {
      const tags = Array.isArray(this.info.tags) ? this.info.tags : []
      return Math.max(tags.length - this.visibleTags.length, 0)
    },
    actionUrl () {
      return this.info.endSessionUrl || this.info.end_session_url || ''
    },
    actionImageUrl () {
      return this.info.actionImageUrl || this.info.action_image_url || ''
    },
    hasActionDetails () {
      return !!(this.actionUrl || this.actionImageUrl)
    },
    actionMetaText () {
      return this.actionUrl || 'Action URL not set'
    },
    statusLabel () {
      if (this.info.startDate !== null && this.info.finishDate === null) {
        return 'In Progress'
      }
      if (this.info.startDate === null && this.info.finishDate === null) {
        return 'To Start'
      }
      return 'Finished'
    },
    editPath () {
      return `/admin/races/edit/${this.info.id}`
    }
  },
  methods: {
    openRun () {
      this.$router.push({ path: this.path })
    },
    formatChip (value) {
      return String(value).replace(/_/g, ' ')
    },
    mountPath () {
      const finalPath = this.type ? `/${this.type}` : ''
      this.path = `/admin/live-run/race/${this.info.id}${finalPath}`
    }
  },
  created () {
    this.mountPath()
  }
}
</script>

<style scoped>
.ritem-card {
  border: 1px solid #e8e8ed;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.ritem-card:hover {
  border-color: #d8d5fb;
  box-shadow: 0 8px 22px rgba(91, 79, 207, 0.1);
  transform: translateY(-1px);
}

.ritem-card:focus-visible {
  outline: 2px solid #5b4fcf;
  outline-offset: 3px;
}

.ritem-media {
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 146px;
  background: #f0effe;
  color: #5b4fcf;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ritem-media--empty {
  border-bottom: 1px solid #e8e8ed;
}

.ritem-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ritem-body {
  min-width: 0;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ritem-topline,
.ritem-chip-row,
.ritem-action-meta,
.ritem-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ritem-topline,
.ritem-actions {
  justify-content: space-between;
}

.ritem-actions {
  justify-content: flex-end;
}

.ritem-status {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: #f0effe;
  color: #5b4fcf;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
  white-space: nowrap;
}

.ritem-date {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 600;
  text-align: right;
}

.ritem-title {
  min-height: 42px;
  margin: 0;
  color: #1a1a2e;
  font-size: 16px;
  line-height: 1.3;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.ritem-chip-row {
  min-width: 0;
  flex-wrap: wrap;
  gap: 6px;
}

.ritem-chip-row--tags {
  margin-top: -4px;
}

.ritem-chip {
  min-height: 23px;
  max-width: 100%;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0 8px;
  font-size: 10px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  white-space: nowrap;
}

.ritem-chip--type {
  background: #f0effe;
  color: #413b94;
}

.ritem-chip--duration {
  gap: 5px;
  background: #fff;
  border: 1px solid #e8e8ed;
  color: #1a1a2e;
}

.ritem-chip--tag {
  background: #fafafa;
  border: 1px solid #e8e8ed;
  color: #1a1a2e;
  text-transform: none;
  font-size: 11px;
  font-weight: 700;
}

.ritem-chip--muted {
  background: #fafafa;
  border: 1px solid #e8e8ed;
  color: #8a8a9a;
}

.ritem-action-meta {
  min-height: 34px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  padding: 5px 8px 5px 5px;
  color: #1a1a2e;
}

.ritem-action-image,
.ritem-action-icon {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  flex: 0 0 auto;
}

.ritem-action-image {
  object-fit: cover;
  display: block;
}

.ritem-action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f0effe;
  color: #5b4fcf;
}

.ritem-action-text {
  min-width: 0;
  flex: 1 1 auto;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ritem-action-open {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #5b4fcf;
  text-decoration: none;
  transition: background 0.18s ease, color 0.18s ease;
}

.ritem-action-open:hover {
  background: #f0effe;
  color: #4338ca;
}

.ritem-icon-actions {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.ritem-edit-btn,
.ritem-delete-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #e8e8ed;
  background: #fff;
  color: #c0392b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
}

.ritem-edit-btn {
  color: #5b4fcf;
  text-decoration: none;
}

.ritem-edit-btn:hover {
  background: #f0effe;
  border-color: #5b4fcf;
  color: #4338ca;
}

.ritem-delete-btn:hover {
  background: #fff4f2;
  border-color: #c0392b;
}

@media (max-width: 620px) {
  .ritem-image,
  .ritem-media {
    min-height: 138px;
  }
}
</style>



// WEBPACK FOOTER //
// RaceItem.vue?3581c496